import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelectedLocation } from "../contexts/SelectedLocationContext";
import { Tab } from "../types/Tab";
import PageSection from "../components/PageSection";
import PageContainer, { GapSize } from "../components/PageContainer";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useShopLink } from "../hooks/useShopLink";

const AboutPage: React.FC = () => {
  const { setDomain, setCategory, setProduct } = useSelectedLocation();
  const shopLink = useShopLink();
  useDocumentMeta({
    title: "About Us",
    description:
      "The story behind The Americana Company — historic Americana clothing and lifestyle goods, printed in the USA.",
  });

  useEffect(() => {
    setDomain(Tab.About.toString());
    setCategory(null);
    setProduct(null);
  }, [setDomain, setCategory, setProduct]);

  useEffect(() => {
    window.scrollTo(0, 0); // Scrolls to the top
  }, []); // Runs only once when the component mounts

  return (
    <PageContainer gapSize={GapSize.Small}>
      <PageSection className="hero">
        <h1>About Us</h1>
        <p>
          We here at The Americana Company want to create beautiful historic
          apparel.
        </p>
      </PageSection>
      <PageSection className="py-4">
        <div className="page-body">
          <p className="page-lead">
            Americana &amp; Company is a small American business with a simple
            mission: to celebrate the history, values, and enduring spirit of
            the United States through timeless design.
          </p>

          {/* The line the whole brand rests on — pulled out of the paragraph
              below rather than buried mid-sentence. */}
          <blockquote className="page-quote">
            Patriotism is not political. It is personal.
          </blockquote>

          <div className="page-prose">
            <p>
              It is pride in where we come from, appreciation for those who came
              before us, and a connection to the ideals that continue to shape
              this country: freedom, resilience, hard work, community, and hope
              for the future.
            </p>

            <p>
              Americana &amp; Company was created for people who want to express
              that pride in a thoughtful and lasting way. Our designs are
              inspired by the symbols, stories, and traditions that have helped
              define the American experience. We aim to create pieces that feel
              classic, meaningful, and authentic. Not loud for the sake of being
              loud. Not driven by passing trends. Just sincere, timeless design
              rooted in American pride.
            </p>

            <p>
              Many brands borrow American imagery when it is convenient, only to
              move away from it when the moment changes. For us, this is not a
              trend or a marketing strategy. Americana is who we are. It is the
              foundation of our brand and the reason we exist.
            </p>

            {/* Origin claim — "designed and printed", never "made". The blank
                garments are imported; the disclosure is on /shipping + /faq. */}
            <p>
              Every design we create is drawn and printed here in the United
              States. That commitment matters to us because we believe products
              inspired by America should be made with care, integrity, and
              respect for the country they represent.
            </p>

            <p>
              At its core, Americana &amp; Company is about more than clothing
              or design. It is about pride, gratitude, and connection. It is for
              those who believe that love of country can be expressed with
              dignity, style, and sincerity.
            </p>

            <p>
              We are proud to build a brand that honors America&apos;s past,
              celebrates its present, and believes in its future.
            </p>
          </div>
        </div>
      </PageSection>

      {/* What the copy above promises, restated as three scannable claims for
          anyone who skims. */}
      <PageSection className="py-4">
        <div className="page-body">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="page-card">
              <h2 className="page-card-title">Rooted in History</h2>
              <p className="page-card-text">
                Inspired by the symbols, stories, and traditions that shaped the
                American experience.
              </p>
            </div>
            <div className="page-card">
              <h2 className="page-card-title">Built to Last</h2>
              <p className="page-card-text">
                Classic, meaningful pieces — never loud for the sake of being
                loud.
              </p>
            </div>
            <div className="page-card">
              <h2 className="page-card-title">Not a Trend</h2>
              <p className="page-card-text">
                Americana is the foundation of our brand, not a passing
                campaign.
              </p>
            </div>
          </div>
        </div>
      </PageSection>

      {/* TODO: Come back to this on who to display — a founders/team row
          (Bryce Robins, Paul Miller, Dylan Aponte, Anelisa Rosario) would slot
          in here as .page-card tiles with photos. */}

      <PageSection className="border-b-2 border-accent pb-10 pt-2">
        <div className="page-body items-center text-center">
          <p className="page-card-text">
            Have a look at what we&apos;ve been making.
          </p>
          <Link to={shopLink} className="btn-primary">
            Shop the Collection
          </Link>
        </div>
      </PageSection>
    </PageContainer>
  );
};

export default AboutPage;
